















































































































import Vue from 'vue';
import moment from 'moment';

import DynamicDocument from '@/containers/dynamic-document';
import FinishConfirmModal from '@/components/service/FinishConfirmModal.vue';
import QuestionnaireFooter from '@/components/service/QuestionnaireFooter.vue';
import AssignmentAttachments from '@/components/service/AssignmentAttachments.vue';

import {
  DocumentImage,
  DynamicDocumentGroup,
  DynamicDocumentItem,
  DynamicDocumentItemData,
  ServiceAssignment,
} from '@/types';
import { translateOrFallback } from '@/helpers/i18n';

import { templateFor } from '@/static/data/forms';
import {
  SectionStatus,
  activeSection,
  attentionPoints,
  isItemComplete,
  openPoints,
  scalarFields,
  sectionStatuses,
  visibleDocument,
} from '@/utilities/dynamic-document';
import parse from '@/store/modules/deliveryDocuments/parse';
import eventbus from '@/utilities/eventbus';

/** Clears the fixed app bar when jumping to a question or a section. */
const SCROLL_OFFSET = 88;

export default Vue.extend({
  components: {
    DynamicDocument,
    FinishConfirmModal,
    QuestionnaireFooter,
    AssignmentAttachments,
  },

  // Inject the service persistence strategy into the dynamic-document engine,
  // overriding its built-in installation/library behavior. `readonly` is exposed
  // as a getter so it stays reactive off `persistenceReadonly`.
  provide(): Record<string, any> {
    const vm = this;

    return {
      documentCtx: {
        get readonly() {
          return vm.persistenceReadonly;
        },
        commitDocument: (data: DynamicDocumentItemData[]) => vm.commitDocument(data),
        uploadPhoto: (file: File, item: DynamicDocumentItem) => vm.uploadPhoto(file, item),
        removePhoto: (image: DocumentImage) => vm.removePhoto(image),
      },
    };
  },

  data() {
    return {
      formData: [] as DynamicDocumentItemData[],
      builtFrom: null as ServiceAssignment | null,
      lastSavedScalars: '',
      showFinishModal: false,
      submitting: false,
      activeAnchor: '',
      scrollFrame: 0,
      // Stable identity: an inline `{}` in the template is a fresh object every render, so the
      // $set writes MultipleChoice makes into it are discarded.
      moduleData: {},

      snackbar: {
        show: false,
        message: '',
        color: 'success',
        timeout: 4000,
      },
    };
  },

  computed: {
    id(): string {
      return this.$route.params.id;
    },

    assignment(): ServiceAssignment | undefined {
      return this.$store.getters['service/details'](this.id);
    },

    // The questionnaire this opdrachtsoort is reported on. Every service soort shares one; retrofit
    // has its own. An unknown soort falls back to the service vragenlijst.
    template(): DynamicDocumentGroup[] {
      return templateFor(this.assignment?.type);
    },

    // What the monteur actually sees: the template with unsatisfied `depends` filtered out.
    // Reactive off formData, so answering a trigger reveals its follow-ups immediately.
    visibleTemplate(): DynamicDocumentGroup[] {
      return visibleDocument(this.template, this.formData);
    },

    templateItems(): DynamicDocumentItem[] {
      return this.template.flatMap(group => group.items);
    },

    itemsByField(): Map<string, DynamicDocumentItem> {
      return new Map(this.templateItems.map(item => [item.field as string, item]));
    },

    // A Set, and cached off the template rather than rebuilt per save: commitDocument runs on every
    // keystroke, and the template only changes when the assignment does.
    scalarFieldSet(): Set<string> {
      return new Set(scalarFields(this.template));
    },

    // Drives the footer tabs: same order as the rendered panels, green once nothing is open.
    sections(): SectionStatus[] {
      return sectionStatuses(this.template, this.formData);
    },

    // Visible required questions still unanswered — what blocks Afronden.
    openPoints(): { field: string; title: string; group: string }[] {
      return openPoints(this.template, this.formData);
    },

    attentionPoints(): { field: string; note: string; answer: string }[] {
      return attentionPoints(this.template, this.formData);
    },

    typeLabel(): string {
      if (!this.assignment) {
        return '';
      }

      return translateOrFallback(
        this.$i18n,
        `service.types.${this.assignment.type}`,
        this.assignment.type,
      );
    },

    workActivities(): string {
      const activities = this.assignment?.work_activities;

      // Backend serves work_activities as a plain-text string with \n breaks (the
      // Partner Portal "Werkomschrijving"). Keep the array fallback defensive only.
      if (Array.isArray(activities)) {
        return activities.join(', ');
      }

      return typeof activities === 'string' ? activities : '';
    },

    formattedStartAt(): string {
      const startAt = this.assignment?.start_at;

      return startAt ? moment(startAt).format('dddd D MMMM YYYY, HH:mm') : '';
    },

    priorityLabel(): string {
      const priority = this.assignment?.priority;

      return priority
        ? translateOrFallback(this.$i18n, `service.detail.priorities.${priority}`, priority)
        : '';
    },

    tenantName(): string {
      return this.assignment?.tenant?.name ?? '';
    },

    tenantPhone(): string {
      return this.assignment?.tenant?.phone ?? '';
    },

    hasAttachments(): boolean {
      return (this.assignment?.attachments?.length ?? 0) > 0;
    },

    // A finished service order stays editable and re-finishable; this only drives
    // the "done" affordance (notice + Finish button label), not a read-only lock.
    isFinished(): boolean {
      return this.assignment?.status === 'finished';
    },

    // Reviewed work (in beoordeling, goedgekeurd, afgekeurd) opens read-only: the backend refuses
    // every write on it, so the questionnaire is shown but cannot be changed or finished again.
    persistenceReadonly(): boolean {
      return this.assignment?.locked === true;
    },
  },

  watch: {
    assignment: {
      immediate: true,
      handler(assignment?: ServiceAssignment) {
        if (!assignment) {
          return;
        }

        // Rebuild whenever the loaded assignment reference changes — e.g. a stale
        // persisted detail is replaced by the fresh fetch. The store detail is
        // immutable while editing (autosave never mutates it), so this never
        // clobbers in-progress edits.
        if (this.builtFrom !== assignment) {
          this.builtFrom = assignment;
          this.buildFormData(assignment);
        }
      },
    },
  },

  created() {
    this.$store.dispatch('service/fetch', this.id);
  },

  mounted() {
    window.addEventListener('scroll', this.onPageScroll, { passive: true });
  },

  beforeDestroy() {
    window.removeEventListener('scroll', this.onPageScroll);
    cancelAnimationFrame(this.scrollFrame);
  },

  methods: {
    /**
     * Seed the engine's data array: scalars from the saved draft, IMAGE fields from the persisted
     * photo Documents (id-bearing, slot-grouped).
     *
     * A row is built for EVERY template item, hidden ones included — DocumentGroup.itemData()
     * asserts a row exists for the item it renders, and a `depends` that later turns true would
     * otherwise reveal a question with no data behind it.
     */
    buildFormData(assignment: ServiceAssignment) {
      const answers = assignment.questionnaire_data ?? {};

      this.formData = this.templateItems.map(item => {
        const field = item.field as string;
        let value: any;

        if (item.type === 'IMAGE') {
          const photos = assignment.photos?.[item.meta?.slot as string] ?? [];
          value = photos.map(photo => ({ url: photo.download_url, id: photo.id }));
        } else if (field in answers) {
          value = answers[field];
        } else {
          value = parse.getDefaultValue(item);
        }

        return { field, value };
      });

      this.recomputeValidity();

      // Baseline so a photo-triggered commit (scalars unchanged) is skipped.
      this.lastSavedScalars = JSON.stringify(this.scalarPayload());

      // The panels only exist once this data has rendered.
      this.$nextTick(this.updateActiveSection);
    },

    recomputeValidity() {
      this.formData.forEach(data => {
        const item = this.itemsByField.get(data.field);

        if (item) {
          this.$set(data, 'isValid', isItemComplete(item, this.formData, this.template));
        }
      });
    },

    scalarPayload(source?: DynamicDocumentItemData[]): Record<string, any> {
      const payload: Record<string, any> = {};

      (source ?? this.formData).forEach(data => {
        if (this.scalarFieldSet.has(data.field)) {
          payload[data.field] = data.value;
        }
      });

      return payload;
    },

    onSaveDocument() {
      this.recomputeValidity();
    },

    async commitDocument(data: DynamicDocumentItemData[]) {
      if (this.persistenceReadonly) {
        return;
      }

      const payload = this.scalarPayload(data);
      const serialized = JSON.stringify(payload);

      // Photos persist through their own endpoints, yet still bubble a
      // save:document — skip the PUT when no scalar answer actually changed.
      if (serialized === this.lastSavedScalars) {
        return;
      }

      this.lastSavedScalars = serialized;
      await this.$store.dispatch('service/saveDraft', { id: Number(this.id), data: payload });
    },

    async uploadPhoto(file: File, item: DynamicDocumentItem): Promise<DocumentImage> {
      const slot = item.meta?.slot;

      const document = await this.$store.dispatch('service/uploadPhoto', {
        id: Number(this.id),
        slot,
        file,
      });

      return { url: document.download_url, id: document.id };
    },

    async removePhoto(image: DocumentImage) {
      await this.$store.dispatch('service/deletePhoto', { id: Number(this.id), doc: image.id });
    },

    async onConfirmFinish() {
      this.submitting = true;

      try {
        await this.$store.dispatch('service/submit', {
          id: Number(this.id),
          data: this.scalarPayload(),
        });

        this.showFinishModal = false;
        this.notify(this.$t('service.questionnaire.finishSuccess') as string, 'success');
        this.$router.push({ name: 'service' });
      } catch (error) {
        this.showFinishModal = false;
        this.handleSubmitError(error);
      } finally {
        this.submitting = false;
      }
    },

    handleSubmitError(error: any) {
      // The /api/mech handler nests validation errors under `data`, not `errors`.
      const errors = error?.response?.data?.data;

      if (error?.response?.status === 422 && errors) {
        const fields = Object.keys(errors)
          .map(field => this.fieldLabel(field))
          .join(', ');

        this.notify(this.$t('service.questionnaire.missingFields', { fields }) as string, 'error');

        return;
      }

      this.notify(this.$t('service.questionnaire.finishError') as string, 'error');
    },

    fieldLabel(field: string): string {
      const item = this.templateItems.find(i => i.field === field);

      return item?.title ?? field;
    },

    // Close the sheet and go to the question, the way the afrondblad in the mockup does — the
    // engine gives every item an id of its field.
    onJumpToOpenPoint(field: string) {
      this.showFinishModal = false;

      this.$nextTick(() => this.$vuetify.goTo(`#${field}`, { offset: SCROLL_OFFSET }));
    },

    onPageScroll() {
      if (this.scrollFrame) {
        return;
      }

      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = 0;
        this.updateActiveSection();
      });
    },

    // Which section the monteur has scrolled to, so the footer can keep its tab in view.
    updateActiveSection() {
      this.activeAnchor =
        activeSection(
          this.sections,
          section => document.getElementById(section.anchor)?.getBoundingClientRect().top,
          this.$vuetify.application.top + 88,
        ) ?? '';
    },

    // Footer tab: open the section if it was collapsed, then bring its header clear of the app bar
    // — the same open-then-scroll the installation screen's item nav does (ItemProgress.navTo).
    onGoToSection(section: SectionStatus) {
      eventbus.$emit('expand:open', section.index);

      this.$nextTick(() => this.$vuetify.goTo(`#${section.anchor}`, { offset: SCROLL_OFFSET }));
    },

    notify(message: string, color: string) {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
  },
});
